export type PreviewCategory =
	| 'image'
	| 'pdf'
	| 'text'
	| 'bpmn'
	| 'audio'
	| 'video'
	| 'word'
	| 'excel'
	| 'powerpoint'
	| 'visio'
	| 'unsupported'

export type OriginalFileDownloadKind = 'audio' | 'video' | 'image' | 'text' | 'document'

export function getOriginalFileDownloadKind(category: PreviewCategory): OriginalFileDownloadKind {
	switch (category) {
		case 'audio':
			return 'audio'
		case 'video':
			return 'video'
		case 'image':
			return 'image'
		case 'text':
		case 'bpmn':
			return 'text'
		default:
			return 'document'
	}
}

/** MIME essence: trim, strip parameters, lowercase — used for classification only */
function normalizedMimeBase(mime_type: string): string {
	return mime_type.trim().split(';')[0]?.trim().toLowerCase() ?? ''
}

export function getPreviewCategory(mime_type: string): PreviewCategory {
	const m = normalizedMimeBase(mime_type)
	if (m.startsWith('image/')) return 'image'
	if (m.includes('pdf')) return 'pdf'
	if (m.startsWith('text/')) return 'text'
	if (m === 'application/bpmn+xml') return 'bpmn'
	if (m === 'application/json' || m === 'application/xml' || m === 'application/sql') return 'text'
	if (m.startsWith('audio/')) return 'audio'
	if (m.startsWith('video/')) return 'video'
	if (m.includes('msword') || m.includes('wordprocessingml')) return 'word'
	if (m.includes('ms-excel') || m.includes('spreadsheetml')) return 'excel'
	if (m.includes('ms-powerpoint') || m.includes('presentationml')) return 'powerpoint'
	if (m.includes('visio') || m.includes('ms-visio')) return 'visio'
	return 'unsupported'
}

/**
 * Preview category using MIME plus artifact fields. Backend may persist `video/webm`
 * as canonical MIME for `.webm` while `type` remains `audio` (browser recordings).
 */
export function getArtifactPreviewCategory(artifact: {
	mime_type: string
	type: string
	extension?: string | null
}): PreviewCategory {
	const category = getPreviewCategory(artifact.mime_type)
	if (category === 'video' && artifact.type.toLowerCase() === 'audio') {
		const base = normalizedMimeBase(artifact.mime_type)
		const ext = (artifact.extension ?? '').toLowerCase().replace(/^\./, '')
		if (base === 'video/webm' || ext === 'webm') return 'audio'
	}
	return category
}

/** Audio and video artifacts share the transcript viewer and tabs. */
export function isTranscriptMediaCategory(category: PreviewCategory): boolean {
	return category === 'audio' || category === 'video'
}
